import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, ShieldCheck, Star } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import TrustRibbon from "@/components/TrustRibbon";
import SEO from "@/components/SEO";
import AutomotiveTintForm from "@/components/AutomotiveTintForm";
import ResidentialTintLanding from "@/components/ResidentialTintLanding";
import WhyTintVideo from "@/components/WhyTintVideo";
import { getService, services } from "@/data/services";

const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } } };

// Auto tint has its own inline form on the page — CTAs scroll to it instead of routing to /mockup
// (which starts with wrap questions and would create friction for tint-only shoppers).
const INLINE_FORM_SLUGS = new Set(["automotive-window-tint"]);

function CTA({ children = "Get My FREE Mockup", testid, location, size = "md", className = "", scrollTo }) {
  const sizes = { md: "!py-3.5 !px-6 text-sm", lg: "!py-4 !px-7 text-base" };
  if (scrollTo) {
    return (
      <a
        href={`#${scrollTo}`}
        data-testid={testid}
        data-track="cta_click"
        data-track-location={location}
        onClick={(e) => {
          e.preventDefault();
          document.getElementById(scrollTo)?.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        className={`btn-red ${sizes[size]} ${className}`}
      >
        {children} <ArrowRight size={16} />
      </a>
    );
  }
  return (
    <Link to="/mockup" data-testid={testid} data-track="cta_click" data-track-location={location} className={`btn-red ${sizes[size]} ${className}`}>
      {children} <ArrowRight size={16} />
    </Link>
  );
}

export default function ServicePage() {
  const { slug } = useParams();
  const s = getService(slug);
  if (!s) return <Navigate to="/services" replace />;

  // Residential tint uses the high-converting Meta LP layout (hero + 3-step form),
  // with the site nav/footer and the FAQ kept below for SEO.
  if (s.slug === "residential-window-tint") {
    return (
      <ResidentialTintLanding
        source="Website — Residential Tint"
        lpKey="residential-tint-website"
        trackLocation="website-residential-tint"
        testidPrefix="svc-res"
        seo={{ title: s.metaTitle, description: s.metaDescription, path: `/services/${s.slug}`, ogImage: s.heroImage }}
      >
        {/* Learn-more video — user-supplied ad creative */}
        <WhyTintVideo />

        {/* FAQ — kept for SEO value */}
        <section className="py-24 md:py-32">
          <div className="wrap max-w-3xl">
            <div className="eyebrow">Common questions</div>
            <h2 className="h-display mt-3" style={{ fontSize: "clamp(36px, 4vw, 56px)" }}>Everything you&apos;d probably <span className="italic font-normal">wonder</span> — answered.</h2>
            <Accordion type="single" collapsible className="mt-12 divide-y divide-neutral-200 border-y border-neutral-200">
              {s.faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-0" data-testid={`svc-${s.slug}-faq-${i}`}>
                  <AccordionTrigger className="text-left font-display text-lg md:text-xl font-bold py-6 hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base text-neutral-700 leading-relaxed pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="mt-12 flex flex-wrap items-center gap-4">
              <p className="text-neutral-500">Still have questions?</p>
              <button
                type="button"
                data-testid={`svc-${s.slug}-faq-cta`}
                data-track="lp_cta_click"
                data-track-location="website_residential_tint_faq"
                onClick={() => document.getElementById("svc-res-form")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="btn-red !py-3.5 !px-6 text-sm"
              >
                Get a free quote <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </section>
      </ResidentialTintLanding>
    );
  }

  const relatedServices = s.related.map((r) => getService(r)).filter(Boolean);
  const hasInlineForm = INLINE_FORM_SLUGS.has(s.slug);
  const ctaScrollTarget = hasInlineForm ? "quote-form" : undefined;
  const ctaLabel = hasInlineForm ? "Get My FREE Quote" : "Get My FREE Mockup";

  return (
    <div data-testid={`page-service-${s.slug}`}>
      <SEO title={s.metaTitle} description={s.metaDescription} path={`/services/${s.slug}`} ogImage={s.heroImage} />

      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-20 overflow-hidden">
        <div className="wrap grid md:grid-cols-12 gap-10 md:gap-12 items-center">
          <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.08 } } }} className="md:col-span-7 relative z-10 flex flex-col md:block">
            <motion.div variants={fadeUp} className="chip">
              <span className="w-2 h-2 rounded-full bg-[var(--red)]" /> {s.eyebrow}
            </motion.div>
            <motion.h1 variants={fadeUp} className="h-display mt-6" style={{ fontSize: "clamp(44px, 4.6vw, 64px)" }}>
              {s.h1}
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-6 text-lg text-neutral-600 max-w-xl leading-relaxed">
              {s.subhead}
            </motion.p>
            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-3">
              <CTA testid={`svc-${s.slug}-hero-cta`} location={`service_${s.slug}_hero`} size="lg" scrollTo={ctaScrollTarget}>{ctaLabel}</CTA>
              {!hasInlineForm && (
                <Link to="/portfolio" className="btn-ghost">View Our Work <ArrowRight size={16} /></Link>
              )}
            </motion.div>
            <motion.div variants={fadeUp} className="order-3 md:order-none mt-8 flex items-center justify-center md:justify-start gap-4 text-sm text-neutral-600">
              <div className="hidden md:flex gap-0.5">
                {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-[var(--red)] text-[var(--red)]" />)}
              </div>
              <span className="font-semibold text-neutral-800">Owner operated since 2012</span>
            </motion.div>
            <motion.div variants={fadeUp} className="order-2 md:order-none mt-6 -mx-6 md:hidden">
              <TrustRibbon compact />
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="md:col-span-5 relative">
            {hasInlineForm ? (
              <div id="quote-form">
                <AutomotiveTintForm />
              </div>
            ) : (
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-[24px] bg-neutral-100">
                <img src={s.heroImage} alt={`${s.name} by 928 Wraps in Prescott Valley, AZ`} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                <div className="absolute left-6 bottom-6 right-6">
                  <div className="eyebrow text-white/80">{s.name}</div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <div className="hidden md:block">
        <TrustRibbon />
      </div>

      {/* WHY THIS MATTERS (PIVOT) */}
      <section className="py-24 md:py-32 bg-[var(--bg-2)]">
        <div className="wrap grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5 md:sticky md:top-32">
            <div className="eyebrow eyebrow-red">{s.pivotEyebrow}</div>
            <h2 className="h-display mt-4" style={{ fontSize: "clamp(36px, 4vw, 56px)" }}>{s.pivotHeadline}</h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-8">
            {s.pivotBody.map((p, i) => (
              <div key={i} className={i > 0 ? "border-t border-neutral-200 pt-8" : ""}>
                <div className="eyebrow">{`0${i + 1}`}</div>
                <p className="mt-3 text-lg text-neutral-700 leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24 md:py-32">
        <div className="wrap">
          <div className="max-w-3xl mb-12">
            <div className="eyebrow">{s.benefitsEyebrow}</div>
            <h2 className="h-display mt-3" style={{ fontSize: "clamp(36px, 4vw, 56px)" }}>{s.benefitsHeadline}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {s.benefits.map((b, i) => (
              <div key={i} className="flat-card rounded-2xl p-8">
                <div className="eyebrow eyebrow-red">{`0${i + 1}`}</div>
                <h3 className="font-display text-2xl mt-4 font-bold">{b.title}</h3>
                <p className="mt-3 text-neutral-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY (optional) */}
      {s.gallery && s.gallery.length > 0 && (
        <section className="pb-24 md:pb-32">
          <div className="wrap">
            <div className="grid md:grid-cols-3 gap-4 md:gap-6">
              {s.gallery.map((g, i) => (
                <div key={i} className="relative overflow-hidden rounded-2xl aspect-[4/3] group">
                  <img src={g.src} alt={`${s.name} — ${g.label}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <div className="eyebrow text-white/80">{g.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PROCESS */}
      <section className="py-24 md:py-32 bg-[var(--bg-2)]">
        <div className="wrap">
          <div className="max-w-3xl mb-16">
            <div className="eyebrow eyebrow-red">{s.processEyebrow}</div>
            <h2 className="h-display mt-3" style={{ fontSize: "clamp(36px, 4vw, 56px)" }}>{s.processHeadline}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative">
            <div className="hidden md:block absolute top-6 left-[16.66%] right-[16.66%] h-[2px] bg-neutral-200" />
            {s.processSteps.map((step) => (
              <div key={step.n} className="relative">
                <div className="w-12 h-12 rounded-full bg-[var(--red)] text-white grid place-items-center font-semibold text-sm relative z-10">{step.n}</div>
                <h3 className="font-display text-2xl mt-6 font-bold">{step.title}</h3>
                <p className="mt-3 text-neutral-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-16 flex flex-wrap items-center gap-4">
            <CTA testid={`svc-${s.slug}-process-cta`} location={`service_${s.slug}_process`} size="lg" scrollTo={ctaScrollTarget}>{ctaLabel}</CTA>
            <span className="text-sm text-neutral-500">90-second form · No pressure · No obligation</span>
          </div>
        </div>
      </section>

      {/* WHY 928 (PILLARS) */}
      <section className="py-24 md:py-32 bg-[var(--ink)] text-white relative overflow-hidden">
        <div className="wrap relative z-10">
          <div className="max-w-3xl">
            <div className="eyebrow eyebrow-red">{s.pillarsEyebrow}</div>
            <h2 className="h-display mt-4" style={{ fontSize: "clamp(36px, 4vw, 56px)" }}>{s.pillarsHeadline}</h2>
          </div>
          <div className="mt-16 grid md:grid-cols-3 gap-12">
            {s.pillars.map((p, i) => (
              <div key={i}>
                <div className="w-11 h-[2px] bg-[var(--red)]" />
                <div className="text-xs text-neutral-500 mt-4 tracking-[0.2em] uppercase font-semibold">{`0${i + 1}`}</div>
                <h3 className="font-display text-2xl md:text-3xl mt-3 font-bold">{p.title}</h3>
                <p className="mt-4 text-neutral-400 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute -right-32 -bottom-32 w-[600px] h-[600px] rounded-full bg-[var(--red)] opacity-[0.05] blur-3xl" />
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32">
        <div className="wrap max-w-3xl">
          <div className="eyebrow">Common questions</div>
          <h2 className="h-display mt-3" style={{ fontSize: "clamp(36px, 4vw, 56px)" }}>Everything you&apos;d probably <span className="italic font-normal">wonder</span> — answered.</h2>
          <Accordion type="single" collapsible className="mt-12 divide-y divide-neutral-200 border-y border-neutral-200">
            {s.faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-0" data-testid={`svc-${s.slug}-faq-${i}`}>
                <AccordionTrigger className="text-left font-display text-lg md:text-xl font-bold py-6 hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-base text-neutral-700 leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <p className="text-neutral-500">Still have questions?</p>
            <CTA testid={`svc-${s.slug}-faq-cta`} location={`service_${s.slug}_faq`} scrollTo={ctaScrollTarget}>{hasInlineForm ? "Get a free quote" : "Start with a free mockup"}</CTA>
          </div>
        </div>
      </section>

      {/* RELATED SERVICES */}
      {relatedServices.length > 0 && (
        <section className="py-16 md:py-24 border-t border-neutral-200 bg-[var(--bg-2)]">
          <div className="wrap">
            <div className="eyebrow">Businesses like yours also invest in</div>
            <div className="mt-6 grid md:grid-cols-3 gap-6">
              {relatedServices.map((r) => (
                <Link key={r.slug} to={`/services/${r.slug}`} data-testid={`svc-related-${r.slug}`} className="flat-card rounded-2xl p-8 block">
                  <div className="eyebrow eyebrow-red">Related</div>
                  <h3 className="font-display text-2xl mt-3 font-bold">{r.name}</h3>
                  <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[var(--ink)]">
                    Explore <ArrowRight size={14} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-24 md:py-32">
        <div className="wrap">
          <div className="border border-neutral-200 rounded-3xl p-10 md:p-16 grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-8">
              <div className="eyebrow eyebrow-red">Your move</div>
              <h3 className="h-display mt-3" style={{ fontSize: "clamp(36px, 4vw, 56px)" }}>Ready to see what {s.name.toLowerCase()} could do for your business?</h3>
              <p className="mt-4 text-neutral-600 max-w-2xl">Free custom mockup. No pressure. Dave replies within 1 business day.</p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <CTA testid={`svc-${s.slug}-final-cta`} location={`service_${s.slug}_final`} size="lg" scrollTo={ctaScrollTarget}>{ctaLabel}</CTA>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
